<script setup>
defineOptions({ name: "AssinadorDigitalModulePage" });
import { defineAsyncComponent } from "vue";
import {
  DocumentTextIcon,
  PaperAirplaneIcon,
  UserGroupIcon,
  ArchiveBoxIcon,
  ShoppingBagIcon,
  WrenchScrewdriverIcon,
  TruckIcon,
  HeartIcon,
  CalendarDaysIcon,
  ShoppingCartIcon,
} from "@heroicons/vue/24/outline";
import { useSeo } from "@/composables/useSeo";
import FaqPanel from "@/components/ui/FAQ.vue";
import SegmentHero from "@/components/ui/SegmentHero.vue";
import heroBg from "@/assets/hero-assdigi.webp";

const CTA = defineAsyncComponent(() => import("@/components/home/CTA.vue"));
const portalUrl =
  import.meta.env?.VITE_PORTAL_URL || "https://portal.kalisistemas.com.br";

useSeo({
  title: "Do documento ao aceite. Tudo digital. | Kali Sistemas",
  description:
    "Envie documentos para assinatura eletrônica direto do Kali Gestão e simplifique aprovações, pedidos e orçamentos, sem papel e sem depender da presença física de quem assina.",
  image: heroBg,
});

// Revela o elemento com fade+slide assim que ele entra na viewport ao rolar a página.
const vReveal = {
  mounted(el) {
    el.classList.add("reveal-init");
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          el.classList.add("reveal-visible");
          io.disconnect();
        }
      },
      { threshold: 0.15, rootMargin: "0px 0px -60px 0px" },
    );
    io.observe(el);
  },
};

// Capacidades reais do adicional Assinador Digital, descritas no material do produto.
const capacidades = [
  {
    icon: DocumentTextIcon,
    titulo: "Documentos do Kali Gestão",
    texto: "Envie contratos, orçamentos, pedidos e outros documentos do sistema direto para assinatura.",
  },
  {
    icon: PaperAirplaneIcon,
    titulo: "Envio para assinatura",
    texto: "Encaminhe o documento sem sair do Kali Gestão, sem anexar em e-mail avulso.",
  },
  {
    icon: UserGroupIcon,
    titulo: "Múltiplos assinantes",
    texto: "Organize quem precisa assinar e, quando necessário, a ordem das assinaturas.",
  },
  {
    icon: ArchiveBoxIcon,
    titulo: "Documentos organizados",
    texto: "Documentos assinados ficam disponíveis para consulta dentro do sistema.",
  },
];

const steps = [
  {
    title: "Escolha o documento",
    description: "Utilize um documento do próprio Kali Gestão ou envie um arquivo PDF.",
  },
  {
    title: "Defina os assinantes",
    description: "Informe quem precisa assinar o documento.",
  },
  {
    title: "Organize o fluxo",
    description: "Quando necessário, determine a sequência em que as pessoas devem assinar.",
  },
  {
    title: "Envie para assinatura",
    description: "O documento fica disponível para assinatura remota.",
  },
  {
    title: "Documento armazenado",
    description: "Após assinado, o documento permanece organizado para consulta.",
  },
];

// Documentos e processos que costumam passar pelo fluxo de assinatura, por segmento.
const segmentos = [
  {
    icon: ShoppingBagIcon,
    titulo: "Comércio",
    texto: "Orçamentos, pedidos de venda e condições comerciais.",
  },
  {
    icon: WrenchScrewdriverIcon,
    titulo: "Prestadores de serviços",
    texto: "Propostas, contratos e termos de aceite.",
  },
  {
    icon: TruckIcon,
    titulo: "Agro",
    texto: "Pedidos, fornecimentos e autorizações.",
  },
  {
    icon: HeartIcon,
    titulo: "Pet",
    texto: "Orçamentos e autorizações relacionadas ao atendimento.",
  },
  {
    icon: CalendarDaysIcon,
    titulo: "Agenda e serviços",
    texto: "Termos e propostas associados ao atendimento.",
  },
  {
    icon: ShoppingCartIcon,
    titulo: "Food",
    texto: "Documentos comerciais e negociações com fornecedores.",
  },
];

const faqItems = [
  {
    question: "Quais documentos posso enviar para assinatura?",
    answer:
      "O Assinador Digital pode trabalhar com documentos compatíveis gerados pelo Kali Gestão, como orçamentos e pedidos, além de arquivos externos em PDF.",
  },
  {
    question: "Posso ter mais de um assinante?",
    answer:
      "Sim. O fluxo pode contemplar diferentes participantes conforme a necessidade do documento.",
  },
  {
    question: "Posso definir quem assina primeiro?",
    answer:
      "Quando o processo exigir uma sequência específica, a ordem das assinaturas pode ser organizada.",
  },
  {
    question: "O cliente precisa estar na empresa para assinar?",
    answer:
      "Não. A proposta do recurso é permitir assinatura remota, direto pelo dispositivo de quem assina.",
  },
  {
    question: "Como solicito a ativação do módulo?",
    answer:
      "Pelo botão \"Solicitar ativação\" desta página, que leva direto ao Portal do Cliente.",
  },
];
</script>

<template>
  <div class="min-h-screen bg-white">
    <SegmentHero title="Do documento ao aceite. Tudo digital."
      description="Envie documentos para assinatura eletrônica direto do Kali Gestão e simplifique aprovações, pedidos, orçamentos, contratos e outros processos que precisam de aceite. Sem impressão, sem papel e sem depender da presença física de quem precisa assinar."
      primary-label="Solicitar ativação" :primary-href="portalUrl" :primary-opens-demo="false"
      secondary-label="Ver Kali Gestão" secondary-href="/solucao/kali-gestao" :background-image="heroBg"
      background-class="bg-slate-900 bg-cover bg-center" overlay-variant="left" />

    <!-- Visão geral -->
    <section class="py-16 bg-white scroll-mt-24">
      <div class="px-4 mx-auto max-w-7xl sm:px-6 lg:px-8">
        <div v-reveal class="max-w-2xl">
          <h2
            class="text-4xl sm:text-5xl font-black tracking-tight !leading-[1.25] pb-2 text-transparent bg-clip-text bg-gradient-to-r from-gray-900 via-[#006086] to-[#004d6b]">
            Não é apenas assinar. É formalizar um processo.
          </h2>
          <p class="mt-6 text-lg leading-8 text-gray-600">
            Em muitas empresas, aprovações ainda dependem de imprimir
            documentos, colher assinaturas manualmente e depois organizar
            tudo de novo. O Assinador Digital transforma esse processo em um
            fluxo digital, preparado, enviado e armazenado dentro da sua
            operação.
          </p>
        </div>

        <div class="grid gap-6 mt-12 sm:grid-cols-2 lg:grid-cols-4">
          <div v-for="cap in capacidades" :key="cap.titulo" v-reveal class="p-6 bg-[#e6f0f4] rounded-2xl">
            <component :is="cap.icon" class="w-8 h-8 text-kali-blue-600" />
            <p class="mt-4 text-base font-bold text-gray-900">{{ cap.titulo }}</p>
            <p class="mt-2 text-sm leading-relaxed text-gray-600">{{ cap.texto }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- Como funciona -->
    <section class="py-16 bg-gray-100 scroll-mt-24">
      <div class="px-4 mx-auto max-w-7xl sm:px-6 lg:px-8">
        <div v-reveal class="max-w-2xl">
          <h2
            class="text-3xl sm:text-4xl font-black tracking-tight !leading-[1.25] pb-1 text-transparent bg-clip-text bg-gradient-to-r from-gray-900 via-[#006086] to-[#004d6b]">
            Do documento à assinatura em poucos passos
          </h2>
          <p class="mt-4 text-base leading-7 text-gray-600">
            Do documento pronto até a assinatura formalizada.
          </p>
        </div>

        <div class="grid gap-6 mt-12 sm:grid-cols-2 lg:grid-cols-5">
          <div v-for="(step, idx) in steps" :key="step.title" v-reveal class="p-7 bg-white rounded-3xl shadow-sm">
            <div
              class="inline-flex items-center justify-center w-10 h-10 font-bold text-white rounded-xl bg-kali-blue-600">
              {{ idx + 1 }}
            </div>
            <h3 class="mt-5 text-lg font-bold text-gray-900">{{ step.title }}</h3>
            <p class="mt-3 text-sm leading-relaxed text-gray-600">
              {{ step.description }}
            </p>
          </div>
        </div>

      </div>
    </section>

    <!-- Aplicações por segmento -->
    <section class="py-16 bg-white scroll-mt-24">
      <div class="px-4 mx-auto max-w-7xl sm:px-6 lg:px-8">
        <div v-reveal class="max-w-2xl">
          <h2
            class="text-3xl sm:text-4xl font-black tracking-tight !leading-[1.25] pb-1 text-transparent bg-clip-text bg-gradient-to-r from-gray-900 via-[#006086] to-[#004d6b]">
            Assinaturas digitais para diferentes operações
          </h2>
        </div>

        <div class="grid gap-6 mt-12 sm:grid-cols-2 lg:grid-cols-3">
          <div v-for="s in segmentos" :key="s.titulo" v-reveal class="p-6 bg-[#e6f0f4] rounded-2xl">
            <component :is="s.icon" class="w-8 h-8 text-kali-blue-600" />
            <p class="mt-4 text-base font-bold text-gray-900">{{ s.titulo }}</p>
            <p class="mt-2 text-sm leading-relaxed text-gray-600">{{ s.texto }}</p>
          </div>
        </div>
      </div>
    </section>

    <FaqPanel title="Dúvidas sobre Assinador Digital" :items="faqItems" cta-label="Solicitar ativação"
      :cta-href="portalUrl"
      description="Respostas rápidas sobre o Assinador Digital. Se preferir, fale com nosso time e te orientamos no melhor cenário." />

    <CTA :activation-href="portalUrl" />
  </div>
</template>

<style scoped>
.reveal-init {
  opacity: 0;
  transform: translateY(28px);
  transition: opacity 0.6s ease-out, transform 0.6s ease-out;
}

.reveal-visible {
  opacity: 1;
  transform: translateY(0);
}

@media (prefers-reduced-motion: reduce) {
  .reveal-init {
    opacity: 1;
    transform: none;
    transition: none;
  }
}
</style>
